import { NgIf, NgFor, NgClass, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, input, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { QuoteSummaryService } from '../../services/direct.quote.summary.service';
import { QuoteSummaryDTO, RfqPartDTO, PartQuantityDTO } from './direct.quote.summary.dto';
import { MixPanelEvents, QuoteStatus, TrackingEvents, TrackingModule } from '../shared/enums';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { OnlyPositiveNumbersDirective } from '../shared/only.positive.numbers.directive';
import { FormatDecimalDirective } from '../directives/format.decimal.directive';
import { FormatNumberPipe } from '../pipes/format.number.pipe';
import { SummaryData } from './direct.quote.summary.model';
import { DirectRfq, RfqPart } from '../direct-quote-assist/direct.rfq.model';
import { DirectQuoteBaseComponent } from '../direct.quote.base.component';
import { DirectQuoteAssistService } from '../../services/direct.quote.assist.service';
import { RfqNameService } from '../services/rfq-name.service';
import { Subscription } from 'rxjs';
import { FileType } from '../rfq-intake/steps/add-parts/add-parts.component';

@Component({
  selector: 'app-direct-quote-summary',
  templateUrl: './direct-quote-summary.component.html',
  imports: [NgIf, NgFor, NgClass, CommonModule, FormsModule, FormatDecimalDirective, OnlyPositiveNumbersDirective, FormatNumberPipe],
  styleUrl: './direct-quote-summary.component.scss'
})

export class DirectQuoteSummaryComponent extends DirectQuoteBaseComponent implements OnInit, OnDestroy {
  @ViewChild('rfqNameInput') rfqNameInput!: ElementRef;

  enableDraftButton = false;
  validationErrors = {
    rfqName: false
  };
  queryParams = {
    rfqId: '',
    companyId: 0,
    rfqPartId: '',
    contactId: 0,
    isTrackingEnabled: 'false',
    integration: '',
    isSkipped: 'false'
  }
  assistData: any;
  assistPart!: RfqPart;
  quoteExpiryMinDate: string;
  currentPart: RfqPartDTO | null = null;
  nextPart: RfqPartDTO | null = null;
  quoteSummaryData: QuoteSummaryDTO = {
    rfqId: 0,
    rfqName: '',
    companyId: 0,
    supplierId: '',
    rfqPartList: [],
    buyerAddress: '',
    buyerEmail: '',
    supplierMetaData: null,
    data: {},
    quoteNumber: '',
    paymentTerm: '',
    isAllPartCompleted: false,
    quoteExpiryDate: '',
    quoteDate: '',
    isQuoteDetailVisible: false,
    isQuoteDetailCompleted: false,
    isQuoteDetailEditable: false,
    isPartSectionVisible: false,
    isPartAdded: false,
    doesAllPartCompleted: (rfqPartList: RfqPartDTO[]): boolean => false
  }
  showReviewQuoteData: boolean = false;
  skippedCaseAmountField = ["tooling", "pricePerUnit", "miscellaneous", "shippingHandling"];
  private rfqNameSubscription: Subscription;

  constructor(
    private directQuoteAssistService: DirectQuoteAssistService,
    private quoteSummaryService: QuoteSummaryService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private rfqNameService: RfqNameService
  ) {
    super();
    const today = new Date();
    this.quoteExpiryMinDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
    this.rfqNameSubscription = this.rfqNameService.rfqName$.subscribe(name => {
      this.quoteSummaryData.rfqName = name;
    });
  }

  ngOnInit() {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['rfqId']) {
        this.queryParams.rfqId = params['rfqId'];
        this.queryParams.companyId = Number(params['companyId']);
        this.queryParams.rfqPartId = params['rfqPartId'];
        this.queryParams.contactId = Number(params['contactId']);
        this.queryParams.isTrackingEnabled = params['isTrackingEnabled'];
        this.queryParams.integration = params['integration'];
        this.queryParams.isSkipped = params['isSkipped'];
        this.isTrackingEnabled = this.queryParams.isTrackingEnabled;
      }

      if (this.queryParams.rfqId) {
        this.quoteSummaryService.loadQuoteSummaryData(this.queryParams).subscribe({
          next: (response: any | null) => {
            const navigationExtras: NavigationExtras = {
              queryParams: {
                rfqId: this.queryParams.rfqId,
                companyId: this.queryParams.companyId,
                rfqPartId: this.queryParams.rfqPartId,
                contactId: this.queryParams.contactId,
                isTrackingEnabled: this.queryParams.isTrackingEnabled,
                integration: this.queryParams.integration,
                isSkipped: this.queryParams.isSkipped
              }
            };

            if(response == null){
              this.router.navigate(['/direct-assist'], navigationExtras);
              return;
            }
            if (response != null &&
              (!response.assistData.rfqPartList[0].quoteAssistData.quoteAssistModel.profit.isSectionCompleted
                && ((this.queryParams.isSkipped ?? 'false') == 'false' && response.assistData.rfqPartList[0].isAssistSkipped == false))) {              
              this.router.navigate(['/direct-assist'], navigationExtras);
              return;
            }

            if (response.assistData) {
              this.assistData = response.assistData;
            }

            if(response.assistData.rfqPartList[0].isAssistSkipped && (this.queryParams.isSkipped ?? 'false') == 'true'){
              if (this.queryParams.isSkipped === 'true') {
                response.assistData.rfqPartList.forEach((part: RfqPartDTO) => {
                  if (part.partQuantity && part.partQuantity.length > 1) {
                    part.partQuantity = [part.partQuantity[0]];
                  }
                });
              }

              this.initializeSummary(response.assistData.rfqPartList[0]);
              this.saveExistingData()?.subscribe(result => {
              });
            }
            
            if (response.rfqDetail) {
              this.quoteSummaryData = response.rfqDetail;
            }
            this.quoteSummaryData.paymentTerm = "";
            this.initializeQuoteSummary(this.quoteSummaryData);
          }
        });
      } else {
        this.saveAndRedirectAssistPage();
      }
    });
  }

  ngOnDestroy() {
    if (this.rfqNameSubscription) {
      this.rfqNameSubscription.unsubscribe();
    }
  }

  initializeQuoteSummary(quoteSummary: QuoteSummaryDTO | null | undefined): void {
    if (quoteSummary == null) {
      console.error("QuoteSummaryDTO is null or undefined");
      return;
    }
    
    // Initialize RFQ name in service when data is loaded
    if (quoteSummary.rfqName) {
      this.rfqNameService.updateRfqName(quoteSummary.rfqName);
    }

    let setNextPartVisible: boolean = true;
    if (this.assistData) {
      this.quoteSummaryData.quoteNumber = this.assistData.quoteNumber;
      this.quoteSummaryData.quoteExpiryDate = this.assistData.quoteExpiryDate;
      this.quoteSummaryData.paymentTerm = this.assistData.paymentTerm;
    }
    const rfqPartList = quoteSummary.rfqPartList;
    if (rfqPartList && Array.isArray(rfqPartList)) {
      for (const rfqPart of rfqPartList) {
        if (rfqPart.isPartCompleted) {
          this.enableDraftButton = true;
        }
        const partQuantity = rfqPart?.partQuantity;
        if (partQuantity && Array.isArray(partQuantity)) {
          for (const partQty of partQuantity) {
            if (partQty != null) {
              partQty.isPartQtyEditable = false;
              partQty.doCalculation = false;
              partQty.qtyCompleted = partQty.summaryData.isCompleted;
              if (partQty.qtyCompleted) {
                this.confirmQuantity(partQty, rfqPart, false); 
                partQty.doCalculation = true;
              }
            }
          }
        } else {
          console.warn("partQuantity is not an array or is undefined/null for rfqPart:", rfqPart);
        }
      }
    } else {
      console.warn("rfqPartList is not an array or is undefined/null in QuoteSummaryDTO");
    }
  }

  downloadCSVFile() {
    this.toastrService.warning("We are working hard to complete this feature as soon as possible");
  }

  partSelectionChange(part: RfqPartDTO) {
    if ((part.isPartCompleted && this.quoteSummaryData) || part.isAssistSkipped) {
      this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
        existingPart.isVisible = (part.rfqPartId == existingPart.rfqPartId);
      });
    }
  }

  showNextPartName(): RfqPartDTO | null {
    this.nextPart = null;
    let isCurrentPartCompleted = false;
    if (this.quoteSummaryData)
      for (const part of this.quoteSummaryData.rfqPartList) {
        if (part.isVisible) {
          this.currentPart = part;
          isCurrentPartCompleted = part.isPartCompleted;

        }
        if (!part.isPartCompleted) {
          if (isCurrentPartCompleted) {
            this.nextPart = part;
            break; // Exit the loop as soon as the condition is met
          }
        }
      }
    return this.nextPart;
  }

  saveAndRedirectToAssistPage(part: RfqPartDTO) {
    var useAssistPage: boolean = false;
    this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
      if (!(existingPart.isPartCompleted) && !existingPart.isAssistSkipped) {
        useAssistPage = true;
      }
    });
    if (useAssistPage) {
      this.saveAndRedirectAssistPage();
    } else {
      const rfqPartList = this.quoteSummaryData.rfqPartList;
      if (rfqPartList && Array.isArray(rfqPartList)) {
        for (const rfqPart of rfqPartList) {
          this.partSelectionChange(rfqPart);
        }
      } else {
        console.warn("rfqPartList is not an array or is undefined/null in QuoteSummaryDTO");
      }
    }
  }

  saveAndRedirectAssistPage() {
    this.showPageDataLoading = true
    this.assistData.quoteNumber = this.quoteSummaryData.quoteNumber;
    this.assistData.quoteExpiryDate = this.quoteSummaryData.quoteExpiryDate;
    this.assistData.paymentTerm = this.quoteSummaryData.paymentTerm;

    this.saveExistingData()?.subscribe(result => {
      this.redirectToAssistPage();
    });
  }

  redirectToAssistPage() {
    if (this.nextPart != null) {
      this.queryParams.rfqPartId = this.nextPart.rfqPartId;
    }
    const navigationExtras: NavigationExtras = {
      queryParams: this.queryParams
    };
    this.router.navigate(['/direct-assist'], navigationExtras);
  }
  quantitySelectionChanged(part: RfqPartDTO, quantityObj: PartQuantityDTO) {
    if (!quantityObj.qtyCompleted) {
      this.toastrService.error("Please complete all required value.");
      return;
    }
    part.partQuantity.forEach((existingQuantityObj: PartQuantityDTO) => {
      existingQuantityObj.isVisible = quantityObj.order === existingQuantityObj.order;
    });
  }

  confirmQuantity(quantityObj: PartQuantityDTO, part: RfqPartDTO, saveData: boolean = true) {

    if (this.isNullOrBlankOrZero(quantityObj.summaryData.pricePerUnit.totalPrice) && saveData) {
      this.toastrService.error("Price Per Unit is required");
      return;
    };
    if (this.isNullOrBlankOrZero(quantityObj.summaryData.leadTime) && saveData) {
      this.toastrService.error("Lead time is required.");
      return;
    }
    if(quantityObj.qty == 0){
      this.toastrService.error("Quantity is required");
      return;
    }
    //TODO other case price can be 0
    quantityObj.isPartQtyEditable = false;
    quantityObj.qtyCompleted = true;
    quantityObj.summaryData.isCompleted = true;
    quantityObj.isVisible = false;
    part.partQuantity.forEach((existingQuantityObj: PartQuantityDTO) => {
      if (quantityObj.order + 1 === existingQuantityObj.order) {
        existingQuantityObj.isVisible = true;
      }
    });
    this.updateAssistSummaryData(quantityObj, part, false);
    part.isPartCompleted = part.doesPartCompleted(part.partQuantity);
    if (part.isPartCompleted) {
      this.enableDraftButton = true;
    }
    if (saveData) {
      this.saveExistingData()?.subscribe(result => { });
    }
    if (this.quoteSummaryData)
      if (this.quoteSummaryData.doesAllPartCompleted(this.quoteSummaryData.rfqPartList)) {
        this.quoteSummaryData.isAllPartCompleted = true;
        // this.createQuoteHandler(false, false);
      }
  }


  saveExistingData() {

    if (!this.assistData) {
      return;
    }
    let quoteSummaryData: DirectRfq = JSON.parse(JSON.stringify(this.assistData));
    return this.quoteSummaryService.updateExistingData(quoteSummaryData);
  }

  reviewQuoteSummary() {
    // Check for zero quantities
    let hasZeroQuantity = false;
    if (this.assistData?.rfqPartList) {
      for (const part of this.assistData.rfqPartList) {
        if (part.partQuantity) {
          for (const quantity of part.partQuantity) {
            if (quantity.qty == 0) {
              hasZeroQuantity = true;
              break;
            }
          }
        }
        if (hasZeroQuantity) break;
      }
    }

    if (hasZeroQuantity) {
      this.toastrService.error("Quantity cannot be zero");
      return;
    }

    // this.pushTrackingDataToMFG("supplier quote assist review quote");
    this.addTracking(this.directQuoteAssistService, TrackingModule.DirectQuoteSummary, TrackingEvents.ReviewQuote, this.assistPart, this.assistData,
      MixPanelEvents.ReviewQuote, this.queryParams);
    this.saveExistingData()?.subscribe(result => {
      this.showReviewQuoteData = true
    })
  }

  updateAssistSummaryData(quantityObj: PartQuantityDTO, inputPart: RfqPartDTO, trackEnabled: boolean = true) {
    if (this.assistData?.rfqPartList) {
      // let assistPart:RfqPart = this.assistData.rfqPartList.find((part:RfqPart) => part.rfqPartId == inputPart.rfqPartId);
      this.assistPart = this.assistData.rfqPartList.find((part: RfqPart) => part.rfqPartId == inputPart.rfqPartId);
      if (this.assistPart) {
        let assistQty = this.assistPart.partQuantity.find(qty => qty.order == quantityObj.order);
        if (assistQty) {
          assistQty.summaryData = quantityObj.summaryData;
        }

        if (trackEnabled) {
          this.addTracking(this.directQuoteAssistService, TrackingModule.DirectQuoteSummary, TrackingEvents.SummaryStarted, this.assistPart, this.assistData,
            MixPanelEvents.SummaryStarted, this.queryParams);
        }
      }
    }
  }

  calculateTotalToolingCost(quantityObj: PartQuantityDTO) {
    let totalCost = 0.0;
    let itemList = quantityObj.summaryData.tooling.toolingList
    if (itemList) {
      itemList.forEach((tooling: any) => {
        totalCost = totalCost + this.convertInternationalToDecimal(this.formatNumbersToFixed2(tooling.estimatedCost))
      });
    }
    return totalCost;
  }

  getTotalCostForGivenQuantity(qtyObj: PartQuantityDTO) {
    let totalCost = 0.0;
    totalCost = totalCost + this.calculateTotalToolingCost(qtyObj);
    return totalCost;
  }

  getSkippedAssistQuantityTotalCost(quantityObj: PartQuantityDTO) {
    quantityObj.summaryData = this.formatNumbersToFixed2(quantityObj.summaryData);
    let totalCost = 0.0;

    this.skippedCaseAmountField.forEach(key => {
      if (key === 'pricePerUnit') {
        const pricePerUnit = quantityObj.summaryData.pricePerUnit;
        const pricePerUnitValue = this.convertInternationalToDecimal(pricePerUnit.totalPrice);
        const qty = this.convertInternationalToDecimal(quantityObj.qty);
        totalCost += pricePerUnitValue * qty;
      } else {
        const amount = quantityObj.summaryData[key as keyof SummaryData] as any;
        totalCost += this.convertInternationalToDecimal(amount.totalCost);
      }
    });
    quantityObj.totalCost = totalCost;
    return totalCost;
  }

  calculateToolingTotal(quantityObj: PartQuantityDTO) {
    let totalCost = this.convertInternationalToDecimal((quantityObj.summaryData.tooling?.toolingList || []).reduce(
      (sum: number, tool: any) => sum + this.convertInternationalToDecimal(tool.estimatedCost.toString()),
      0
    ));
    quantityObj.summaryData.tooling.cost = this.formatNumbersToFixed2(totalCost);
    quantityObj.summaryData.tooling.profitValue = this.convertInternationalToDecimal((totalCost * ((this.convertInternationalToDecimal(quantityObj.summaryData.tooling.profitMargin) / 100))))
    return this.convertInternationalToDecimal((totalCost + quantityObj.summaryData.tooling.profitValue).toFixed(2))

  }

  calculateShippingTotal(quantityObj: PartQuantityDTO) {
    let totalCost = this.convertInternationalToDecimal(quantityObj.summaryData.shippingHandling.cost);
    quantityObj.summaryData.shippingHandling.cost = this.formatNumbersToFixed2(totalCost);
    quantityObj.summaryData.shippingHandling.profitValue = this.convertInternationalToDecimal((totalCost * ((this.convertInternationalToDecimal(quantityObj.summaryData.shippingHandling.profitMargin) / 100))))
    return this.convertInternationalToDecimal((totalCost + quantityObj.summaryData.shippingHandling.profitValue).toFixed(2))

  }

  calculatePricePerUnitCost(quantityObj: PartQuantityDTO) {
    const { materials, machining, labor, additionalManufacturingRequirements, overhead } = quantityObj.summaryData.pricePerUnit;
    const materialsCost = this.convertInternationalToDecimal(materials.totalCost);
    const machiningCost = this.convertInternationalToDecimal(machining.totalCost)
    const laborCost = this.convertInternationalToDecimal(labor.totalCost);
    const amrCost = this.convertInternationalToDecimal(additionalManufacturingRequirements.totalCost);
    const overheadCost = this.convertInternationalToDecimal(overhead.totalCost);
    let pricePerUnitValue = (
      (materialsCost) +
      (machiningCost) +
      laborCost +
      amrCost +
      overheadCost
    );
    quantityObj.summaryData.pricePerUnit.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal(pricePerUnitValue) * (this.convertInternationalToDecimal(quantityObj.summaryData.pricePerUnit.profitMargin) / 100));
    // quantityObj.summaryData.pricePerUnit.profitValue = this.convertInternationalToDecimal((((this.formatNumbersToFixed2(pricePerUnitValue)) * ((this.convertInternationalToDecimal(this.formatNumbersToFixed2(quantityObj.summaryData.pricePerUnit.profitMargin)) / 100)))).toFixed(2));
  }

  calculateMiscellaneousCost(quantityObj: PartQuantityDTO) {
    const { miscellaneous } = quantityObj.summaryData;
    // const qty = this.tryParseInt(quantityObj.qty);
    const miscellaneousCost = this.convertInternationalToDecimal(miscellaneous.itemList.reduce(
      (acc: number, otherCost: { estimatedCost: number }) => {
        return acc + this.convertInternationalToDecimal(otherCost.estimatedCost)
      }, 0
    ));
    quantityObj.summaryData.miscellaneous.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal(miscellaneousCost) * (this.convertInternationalToDecimal(quantityObj.summaryData.miscellaneous.profitMargin) / 100));
    // quantityObj.summaryData.miscellaneous.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal((this.formatNumbersToFixed2(miscellaneousCost)) * ((this.convertInternationalToDecimal(this.formatNumbersToFixed2(quantityObj.summaryData.miscellaneous.profitMargin)) / 100))).toFixed(2));
  }

  calculateTotalCost(quantityObj: PartQuantityDTO): number {
    const { summaryData } = quantityObj;

    const { materials, machining, labor, additionalManufacturingRequirements, overhead } = summaryData.pricePerUnit;
    const { tooling, miscellaneous, shippingHandling, pricePerUnit } = summaryData;

    const qty = this.tryParseInt(quantityObj.qty);
    const materialsCost = quantityObj.doCalculation ? this.convertInternationalToDecimal(materials.totalCost) : materials.unitCost * qty;
    const machiningCost = quantityObj.doCalculation ? this.convertInternationalToDecimal(machining.totalCost) : this.convertInternationalToDecimal(machining.machineList.reduce(
      (acc: number, item: { costPerMin: number; cyclePerUnit: number }) => acc + this.convertInternationalToDecimal(item.costPerMin) * this.convertInternationalToDecimal(item.cyclePerUnit), 0
    ) * qty);
    const laborCost = this.convertInternationalToDecimal(labor.totalCost);
    const amrCost = this.convertInternationalToDecimal(additionalManufacturingRequirements.totalCost);
    const overheadCost = this.convertInternationalToDecimal(overhead.totalCost);

    const toolingCost = this.convertInternationalToDecimal(tooling.cost);
    const miscellaneousCost = this.convertInternationalToDecimal(miscellaneous.itemList.reduce(
      (acc: number, otherCost: { estimatedCost: number }) => {
        return acc + this.convertInternationalToDecimal(otherCost.estimatedCost)
      }, 0
    ));
    const shippingHandlingCost = this.convertInternationalToDecimal(shippingHandling.cost);
    materials.totalCost = this.convertInternationalToDecimal(materialsCost);
    machining.totalCost = this.convertInternationalToDecimal(machiningCost);
    // Calculate price per unit
    let pricePerUnitValue = this.convertInternationalToDecimal(((
      (materialsCost) +
      (machiningCost) +
      laborCost +
      amrCost +
      overheadCost
    )));

    if (!quantityObj.doCalculation)
      tooling.profitValue = this.convertInternationalToDecimal((toolingCost * ((this.convertInternationalToDecimal(summaryData.tooling.profitMargin) / 100))));
    tooling.totalCost = this.convertInternationalToDecimal((toolingCost + this.convertInternationalToDecimal(tooling.profitValue)));

    if (!quantityObj.doCalculation)
      pricePerUnit.profitValue = this.convertInternationalToDecimal((((pricePerUnitValue) * ((this.convertInternationalToDecimal(summaryData.pricePerUnit.profitMargin) / 100)))));
    pricePerUnit.totalPrice = this.convertInternationalToDecimal((((pricePerUnitValue) + this.convertInternationalToDecimal(pricePerUnit.profitValue)) / qty));

    if (!quantityObj.doCalculation)
      miscellaneous.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal((miscellaneousCost) * ((this.convertInternationalToDecimal(summaryData.miscellaneous.profitMargin) / 100))));
    miscellaneous.totalCost = this.convertInternationalToDecimal(this.convertInternationalToDecimal((miscellaneousCost) + this.convertInternationalToDecimal(miscellaneous.profitValue)));

    if (!quantityObj.doCalculation)
      shippingHandling.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal((shippingHandlingCost) * ((this.convertInternationalToDecimal(summaryData.shippingHandling.profitMargin) / 100))));
    shippingHandling.totalCost = this.convertInternationalToDecimal(this.convertInternationalToDecimal((shippingHandlingCost) + this.convertInternationalToDecimal(shippingHandling.profitValue)));


    const totalCost =
      this.convertInternationalToDecimal((tooling.totalCost + (pricePerUnit.totalPrice * qty) + miscellaneous.totalCost + shippingHandling.totalCost));


    quantityObj.doCalculation = true;
    quantityObj.totalCost = totalCost;
    return totalCost;
  }

  createQuoteHandler(isSubmit: boolean = true, isDraft: boolean = false, redirectToList: boolean = true) {
    const quoteSummaryData = JSON.parse(JSON.stringify(this.quoteSummaryData));

    this.validationErrors.rfqName = false;
    if (!quoteSummaryData.rfqName?.trim() || quoteSummaryData.rfqName === 'Enter RFQ Name') {
      this.validationErrors.rfqName = true;
      return;
    }

    const hasInvalidLeadTime = quoteSummaryData.rfqPartList.some((part: any) =>
      part.partQuantity.some((quantity: any) =>
        !quantity.summaryData.leadTime || Number(quantity.summaryData.leadTime) === 0
      )
    );

    if (hasInvalidLeadTime) {
      this.toastrService.error("Lead Time cannot be empty or zero");
      return;
    }

    if (isSubmit) {
      if (this.isNullOrBlankOrEmpty(quoteSummaryData.quoteNumber)) {
        this.toastrService.error("Quote Reference Number is required");
        return;
      }
      if (this.isNullOrBlankOrEmpty(quoteSummaryData.quoteExpiryDate)) {
        this.toastrService.error("Quote Expiry Date is required");
        return;
      }
    }
    this.showPageDataLoading = true
    const paymentTermObj = this.paymentTermList.filter(term => {
      return term.code === quoteSummaryData.paymentTerm;
    })[0];
    let rfqData: any = {
      rfqId: quoteSummaryData.rfqId,
      quoteExpiryDate: quoteSummaryData.quoteExpiryDate,
      quoteReferenceNumber: quoteSummaryData.quoteNumber,
      paymentTerms: paymentTermObj,
      isSubmitted: isSubmit
    };

    this.assistData.quoteNumber = this.quoteSummaryData.quoteNumber;
    this.assistData.quoteExpiryDate = this.quoteSummaryData.quoteExpiryDate;
    this.assistData.paymentTerm = this.quoteSummaryData.paymentTerm;

    // let quoteData: any = {};
    // if(this.assistData.rfqPartList[0].isAssistSkipped){
    //   quoteData = JSON.stringify(this.assistData);
    // }else{
    //   quoteData = JSON.stringify(JSON.parse(JSON.stringify(this.assistData)));
    // }

    const payload = {
      CompanyId: this.queryParams.companyId,
      SupplierId: this.queryParams.contactId,
      RfqId: this.queryParams.rfqId,
      // QuoteStatus: isSubmit ? QuoteStatus.Quoted : QuoteStatus.Draft,
      QuoteStatus: isSubmit ? QuoteStatus.Quoted :
        isDraft ? QuoteStatus.Draft :
        this.quoteSummaryData.doesAllPartCompleted(this.quoteSummaryData.rfqPartList) ? 
          QuoteStatus.ReadyToSend : QuoteStatus.Draft,
      RfqPartsMetaData: JSON.stringify(this.assistData),
      Source: this.queryParams?.integration
    };

    this.quoteSummaryService.saveDraft(payload).subscribe({
      next: (response: any) => {
        if (response.status) {

          //Update RFQ Name
          const formData = this.createRfqFormData(quoteSummaryData.rfqName);
          this.directQuoteAssistService.saveDirectRFQDetails(formData).subscribe({
            next: (response: any) => {
            },
            error: (error) => {
              console.error('Error updating RFQ details:', error);
            }
          });
          //Update RFQ Name

          this.saveExistingData()?.subscribe(result => {
            let rfqPartList: any = [];
            let notValid = false;
            quoteSummaryData.rfqPartList.forEach((part: any) => {
              if (part.isPartCompleted) {
                let rfqPart: any = {
                  rfqPartId: part.rfqPartId,
                  rfqPartName: part.rfqPartName
                };

                let partQuantityList: any = [];
                part.partQuantity.forEach((quantityObj: any) => {
                  let assistedData: any = {};
                  assistedData["rfqPartId"] = part.rfqPartId;
                  assistedData["rfqPartQuantityId"] = quantityObj.rfqPartQuantityId;
                  assistedData["quantityLevel"] = quantityObj.order;
                  assistedData["perUnitPrice"] = quantityObj.summaryData.pricePerUnit.totalPrice;
                  assistedData["shippingAmount"] = quantityObj.summaryData.shippingHandling.totalCost
                  assistedData["toolingAmount"] = quantityObj.summaryData.tooling.totalCost
                  assistedData["estLeadTimeValue"] = Number(quantityObj.summaryData.leadTime);
                  assistedData["awardedQty"] = quantityObj.qty;
                  assistedData["estLeadTimeRange"] = "Days"
                  assistedData["miscellaneousAmount"] = quantityObj.summaryData.miscellaneous.totalCost;
                  if (
                    this.isNullOrBlankOrZero(assistedData["perUnitPrice"]) ||
                    this.isNullOrBlankOrZero(assistedData["estLeadTimeValue"])
                  ) {
                    notValid = true;
                  }
                  partQuantityList.push(assistedData);
                });
                rfqPart.partQuantityList = partQuantityList;
                rfqPartList.push(rfqPart);
              }
            });
            rfqData["rfqPartList"] = rfqPartList;
            if (notValid) {
              this.showPageDataLoading = false;
              this.toastrService.warning("Per Unit Price and Lead Time cannot zero");
              return;
            }
            if (rfqData["rfqPartList"].length < 1) {
              this.showPageDataLoading = false;
              console.log("No part is completed");
              this.toastrService.error("At least one part should be completed");
              return;
            }

            let trackingEventName = isSubmit ? TrackingEvents.QuoteCreated : TrackingEvents.SaveToDraft;
            let mixPanelEventName = isSubmit ? MixPanelEvents.QuoteCreated : MixPanelEvents.SaveToDraft;

            this.addTracking(this.directQuoteAssistService, TrackingModule.DirectQuoteSummary, 
              trackingEventName, this.assistPart, this.assistData,
              mixPanelEventName, this.queryParams);

            if (redirectToList) {
              this.toastrService.success(isSubmit ? "Quote created successfully" : "Draft saved successfully");
              if (window.parent) {
                setTimeout(() => {
                  window.parent.postMessage({
                    closeIframePopup: true
                  }, '*');
                }, 700); 
              }
            }
          });
        }
        else {
          this.toastrService.error(isSubmit ? "Quote creation failed" : "Quote save failed");
        }
        this.showPageDataLoading = false;
      },
      error: (error: any) => {
        console.error('Error saving draft:', error);
      }
    });
  }

  pushTrackingDataToMFG(eventName: string, dataToSend: any = {}) {
    if (window.parent) {
      let dataToEmit: any = Object.assign({ isQuoteAssistTracking: true }, this.queryParams);
      dataToEmit = Object.assign(dataToEmit, dataToSend)
      dataToEmit["eventName"] = eventName
      window.parent.postMessage(dataToEmit, "*");
    }
  }
  backToAssist() {
    this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
      if (existingPart.isAssistSkipped && !existingPart.isPartCompleted) {
        existingPart.isAssistSkipped = false;
        existingPart.isAssistEnabled = true;
      }
    });
    this.saveAndRedirectAssistPage();
  }
  saveDraft() {
    this.createQuoteHandler(false, true);
  }

  calculatePricePerUnitCostAI(quantityObj: PartQuantityDTO) {
    const { materials, machining, labor, additionalManufacturingRequirements, overhead } = quantityObj.summaryData.pricePerUnit;
    const materialsCost = this.convertInternationalToDecimal(materials.totalCost);
    const machiningCost = this.convertInternationalToDecimal(machining.totalCost)
    const laborCost = this.convertInternationalToDecimal(labor.totalCost);
    const amrCost = this.convertInternationalToDecimal(additionalManufacturingRequirements.totalCost);
    const overheadCost = this.convertInternationalToDecimal(overhead.totalCost);
    let pricePerUnitValue = (
      (materialsCost) +
      (machiningCost) +
      laborCost +
      amrCost +
      overheadCost
    );
    let totalPrice = this.convertInternationalToDecimal(this.convertInternationalToDecimal(pricePerUnitValue) * (1 + (this.convertInternationalToDecimal(quantityObj.summaryData.pricePerUnit.profitMargin) / 100)));
    return totalPrice;
    // quantityObj.summaryData.pricePerUnit.profitValue = this.convertInternationalToDecimal((((this.formatNumbersToFixed2(pricePerUnitValue)) * ((this.convertInternationalToDecimal(this.formatNumbersToFixed2(quantityObj.summaryData.pricePerUnit.profitMargin)) / 100)))).toFixed(2));
  }

  addQuantityAI(part: RfqPartDTO) {
    if (part.partQuantity.length == 3) {
      this.toastrService.error("You can only add up to 3 quantities");
      return;
    }

    const sourceQuantity = part.partQuantity[0];
    const newQuantityObj = JSON.parse(JSON.stringify(sourceQuantity));

    newQuantityObj.order = part.partQuantity.length + 1;
    newQuantityObj.qty = Number(sourceQuantity.qty);
    newQuantityObj.isVisible = false;
    newQuantityObj.doCalculation = false;
    newQuantityObj.isPartQtyEditable = true;

    part.partQuantity.push(newQuantityObj);
  }
  private previousQty: number = 0;
  onQuantityChange(quantityObj: any): void {
    if (quantityObj.qty == 0) {
      this.toastrService.error("Quantity cannot be zero");
      // quantityObj.qty = this.previousQty;
      return;
    }
    if (isNaN(quantityObj.qty)) {
      this.toastrService.error("Quantity must be a number");
      // quantityObj.qty = this.previousQty;
      return;
    }
    quantityObj.doCalculation = false;
    this.getSkippedAssistQuantityTotalCost(quantityObj);
    // this.calculateTotalCost(quantityObj);
  }
  startEditing(quantityObj: any) {
    quantityObj.isPartQtyEditable = true;
    setTimeout(() => {
      const input = document.querySelector('input') as HTMLInputElement;
      if (input) input.focus();
    }, 0);
  }

  stopEditing(quantityObj: any) {
    quantityObj.isPartQtyEditable = false;
  }

  showQuoteDetailSection(){
    this.assistData.isQuoteDetailVisible = true;
    this.assistData.isAssistSectionVisible = false;
    // this.rfqData.isQuoteDetailVisible = true;
    // this.rfqData.isAssistSectionVisible = false;
    this.saveExistingData()?.subscribe(result => {
      this.router.navigate(['/direct-assist'], {
        queryParams: {
          rfqId: this.queryParams.rfqId,
          companyId: this.queryParams.companyId,
          rfqPartId: this.queryParams.rfqPartId,
          contactId: this.queryParams.contactId,
          integration: this.queryParams.integration,
          isTrackingEnabled: this.queryParams.isTrackingEnabled
        }
      });
    });    
  }
  showAssistSection(){
    this.assistData.isQuoteDetailVisible = false;
    this.assistData.isAssistSectionVisible = true;
    this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
      if (existingPart.isAssistSkipped && !existingPart.isPartCompleted) {
        existingPart.isAssistSkipped = false;
        existingPart.isAssistEnabled = true;
      }
    });
    this.saveAndRedirectAssistPage();
    // this.router.navigate(['/direct-assist'], {
    //   queryParams: {
    //     rfqId: this.queryParams.rfqId,
    //     companyId: this.queryParams.companyId,
    //     rfqPartId: this.queryParams.rfqPartId,
    //     contactId: this.queryParams.contactId
    //   }
    // });
  }

  initializeSummary(part: RfqPartDTO) {
    const quantities = [part.qty1, part.qty2, part.qty3];
    part.partQuantity = [];
    quantities.forEach((qty, index) => {
      if (qty && qty > 0) {
        part.partQuantity.push({
          order: index + 1,
          qty: qty,
          qtyCompleted: false,
          isAssisted: false,
          isPartQtyEditable: false,
          doCalculation: false,
          isVisible: true,
          quantityUnit: "Unit",
          totalCost: 0,
          summaryData: {
            isCopied: false,
            isCompleted: false,
            isDataLoaded: false,
            tooling: { totalCost: 0, cost: 0, toolingList: [], profitMargin: 0, profitValue: 0 },
            pricePerUnit: {
              materials: { totalCost: 0, unitCost: 0 },
              machining: { totalCost: 0, machineList: [{ cyclePerUnit: 0, costPerMin: 0 }] },
              labor: { totalCost: 0 },
              additionalManufacturingRequirements: { totalCost: 0 },
              overhead: { totalCost: 0 },
              totalPrice: 0,
              profitMargin: 0,
              profitValue: 0,
            },
            miscellaneous: { cost: 0, totalCost: 0, itemList: [], profitMargin: 0, profitValue: 0 },
            shippingHandling: { cost: 0, totalCost: 0, profitMargin: 0, profitValue: 0 },
            leadTime: ""
          }
        });
      }
    });

    if (part.partQuantity.length === 0) {
      part.partQuantity.push({
        order: 1,
        qty: 0,
        qtyCompleted: false,
        isAssisted: false,
        isPartQtyEditable: false,
        doCalculation: false,
        isVisible: true,
        quantityUnit: "Unit",
        totalCost: 0,
        summaryData: {
          isCopied: false,
          isCompleted: false,
          isDataLoaded: false,
          tooling: { totalCost: 0, cost: 0, toolingList: [], profitMargin: 0, profitValue: 0 },
          pricePerUnit: {
            materials: { totalCost: 0, unitCost: 0 },
            machining: { totalCost: 0, machineList: [{ cyclePerUnit: 0, costPerMin: 0 }] },
            labor: { totalCost: 0 },
            additionalManufacturingRequirements: { totalCost: 0 },
            overhead: { totalCost: 0 },
            totalPrice: 0,
            profitMargin: 0,
            profitValue: 0,
          },
          miscellaneous: { cost: 0, totalCost: 0, itemList: [], profitMargin: 0, profitValue: 0 },
          shippingHandling: { cost: 0, totalCost: 0, profitMargin: 0, profitValue: 0 },
          leadTime: ""
        }
      });
    }
  }

  private createRfqFormData(rfqName?: string): FormData {
    const formData = new FormData();
    formData.append('CompanyId', this.queryParams.companyId.toString());
    formData.append('RfqId', this.queryParams.rfqId.toString());
    formData.append('RfqName', rfqName || this.assistData?.rfqName || '');
    formData.append('QuoteDate', this.quoteSummaryData.quoteExpiryDate || new Date().toISOString());
    formData.append('CreatedOn', new Date().toISOString());
    formData.append('Source', this.queryParams.integration);
    
    // Add null checks for billTo data
    const billTo = this.assistData?.billTo || {};
    formData.append('BuyerCompanyName', billTo.companyName || '');
    formData.append('BuyerCompanyAddress', billTo.address || '');
    formData.append('BuyerCompanyEmail', billTo.companyEmail || '');
    formData.append('BuyerCompanyPhone', billTo.companyPhoneNumber || '');
    formData.append('BuyerCompanyCity', billTo.city || '');
    formData.append('BuyerCompanyState', billTo.state || '');
    formData.append('BuyerCompanyZipCode', billTo.zipCode || '');
    formData.append('BuyerAddress', '');

    // Add RFQ Part Details
    formData.append('RfqPartDetails.IsUpdated', 'false');
    if (this.currentPart) {
      formData.append('RfqPartDetails.Qty1', this.currentPart.qty1?.toString() || '0');
      formData.append('RfqPartDetails.PartName', this.currentPart.partName || '');
      formData.append('RfqPartDetails.PartId', '1');
    }

    // Add RFQ Part Files with null check
    const rfqPartDetails = this.assistData?.rfqPartDetails;
    const files = rfqPartDetails?.files || [];

    if (files.length > 0) {
      files.forEach((fileObj: any, index: number) => {
        // Always use the current index and fileObj's properties
        formData.append(`RfqPartDetails.Files[${index}].File`, fileObj?.file || "");
        formData.append(`RfqPartDetails.Files[${index}].FileName`, fileObj?.fileName || "");
        formData.append(`RfqPartDetails.Files[${index}].IsUpdated`, fileObj?.isUpdated ? "true" : "false");
        formData.append(`RfqPartDetails.Files[${index}].FileType`, fileObj?.fileType || FileType.Drawing2D);
      });
    } else {
      formData.append('RfqPartDetails.Files', JSON.stringify([]));
    }

    return formData;
  }

  onRfqNameUpdate(newName: string) {
    this.rfqNameService.updateRfqName(newName);
    
    if (this.assistData) {
      this.assistData.rfqName = newName;
      
      const formData = this.createRfqFormData(newName);
      this.directQuoteAssistService.saveDirectRFQDetails(formData).subscribe({
        next: (response: any) => {
          // RFQ details updated successfully
        },
        error: (error) => {
          console.error('Error updating RFQ details:', error);
          // this.toastrService.error("Failed to update RFQ name.");
        }
      });
    }
  }

  onRfqNameFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    if (!input.value.trim()) {
      input.value = 'Enter RFQ Name';
      input.select();
      this.quoteSummaryData.rfqName = 'Enter RFQ Name';
    } else if (input.value === 'Enter RFQ Name') {
      input.select();
    }
  }

  onRfqNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // Trim leading spaces and update the input value
    const trimmedValue = input.value.replace(/^\s+/, '');
    if (trimmedValue !== input.value) {
      input.value = trimmedValue;
      this.quoteSummaryData.rfqName = trimmedValue;
    }
    
    if (!trimmedValue) {
      this.validationErrors.rfqName = true;
      return;
    }
    
    this.validationErrors.rfqName = false;
  }

  onRfqNameBlur() {
    if (!this.quoteSummaryData.rfqName || !this.quoteSummaryData.rfqName.trim() || this.quoteSummaryData.rfqName === 'Enter RFQ Name') {
      this.validationErrors.rfqName = true;
      return;
    }
    
    this.validationErrors.rfqName = false;
    this.onRfqNameUpdate(this.quoteSummaryData.rfqName);
    // Make the input lose focus
    if (this.rfqNameInput) {
      this.rfqNameInput.nativeElement.blur();
    }
  }
}